// Direction helpers shared by Case Buddy report and address-label utilities.
// Spreadsheet-shaped Main Data remains the source of truth; direction is inferred
// from the existing Flow Solver / Tool 88 notes already stored with each entry.
import { normalizeCell } from './CaseBuddyShared.js';
import { isMainTransactionPathLabel, parseCaseEntries } from './CaseBuddySheetData.js';
import { normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_TRACE_DIRECTION_FORWARD = 'forward';
export const CASE_TRACE_DIRECTION_REVERSE = 'reverse';

export function normalizeCaseTraceDirection(value = '') {
  const text = normalizeCell(value).toLowerCase();
  if (text === CASE_TRACE_DIRECTION_REVERSE || text === 'backward' || text === 'backwards') {
    return CASE_TRACE_DIRECTION_REVERSE;
  }
  if (text === CASE_TRACE_DIRECTION_FORWARD) return CASE_TRACE_DIRECTION_FORWARD;
  return '';
}

function collectCaseTraceDirectionMarkers(text = '') {
  const source = String(text || '');
  if (!source) return [];
  const markers = [];
  const patterns = [
    /\bdirection\s*[:=]\s*(forward|reverse)\b/giu,
    /\btrace[_\s-]*direction\s*[:=]\s*(forward|reverse)\b/giu,
    /\b(?:tool\s*88|flow\s+solver)[^\r\n.;]{0,120}?\b(forward|reverse)\b/giu,
    /\b(forward|reverse)\s+(?:fifo|lifo|next[_\s-]*chronological|trace(?:d|s|ing)?)\b/giu
  ];

  patterns.forEach((pattern) => {
    let match;
    while ((match = pattern.exec(source)) !== null) {
      const direction = normalizeCaseTraceDirection(match[1]);
      if (direction) markers.push({ index: match.index, direction });
      if (match[0] === '') pattern.lastIndex += 1;
    }
  });

  return markers.sort((left, right) => left.index - right.index);
}

export function detectCaseTraceDirectionFromText(text = '') {
  const markers = collectCaseTraceDirectionMarkers(text);
  return markers.length ? markers[markers.length - 1].direction : '';
}

export function detectCaseTraceDirectionFromRow(row = []) {
  if (!Array.isArray(row)) return '';

  // Column W contains the explicit Flow Solver record and is authoritative when
  // present. Columns J/L are compatibility fallbacks for older case rows.
  for (const columnIndex of [22, 9, 11]) {
    const direction = detectCaseTraceDirectionFromText(row[columnIndex]);
    if (direction) return direction;
  }
  return '';
}

export function getCaseTraceEntryPathLabel(entry = {}) {
  for (const item of (Array.isArray(entry?.rows) ? entry.rows : [])) {
    const value = normalizeCell(item?.row?.[0]);
    if (value) return value;
  }
  return '';
}

export function getCaseTraceEntryFromHash(entry = {}) {
  for (const item of (Array.isArray(entry?.rows) ? entry.rows : [])) {
    const value = normalizeCell(item?.row?.[14]);
    if (value) return value;
  }
  return '';
}

export function detectCaseTraceDirectionForEntry(entry = {}) {
  let direction = '';
  (Array.isArray(entry?.rows) ? entry.rows : []).forEach((item) => {
    const nextDirection = detectCaseTraceDirectionFromRow(item?.row || []);
    if (nextDirection) direction = nextDirection;
  });
  return direction;
}

function caseTracePathBelongsToRoot(pathLabel = '', rootLabel = '') {
  const path = normalizeCell(pathLabel);
  const root = normalizeCell(rootLabel);
  if (!path || !root) return false;
  if (path === root) return true;
  if (!path.startsWith(root)) return false;

  const remainder = path.slice(root.length);
  if (!remainder) return true;

  // Numeric and prefix-numeric roots should not make root 1 claim root 10.
  if (/\d$/.test(root) && /^\d/.test(remainder)) return false;
  return true;
}

function findCaseTraceRootEntry(entry = {}, rootEntries = []) {
  const pathLabel = getCaseTraceEntryPathLabel(entry);
  if (pathLabel) {
    const matches = rootEntries
      .filter(root => caseTracePathBelongsToRoot(pathLabel, root.pathLabel))
      .sort((left, right) => right.pathLabel.length - left.pathLabel.length || left.startRow - right.startRow);
    if (matches.length) return matches[0];
  }
  return null;
}

function findCaseTraceParentEntry(entry = {}, entries = []) {
  const fromHash = getCaseTraceEntryFromHash(entry);
  if (!fromHash) return null;
  const key = fromHash.toLowerCase();
  const before = entries
    .filter(candidate => candidate !== entry && normalizeCell(candidate?.hash).toLowerCase() === key && candidate.startRow < entry.startRow)
    .sort((left, right) => right.startRow - left.startRow);
  return before[0] || null;
}

export function buildCaseTraceDirectionContextByRowIndex(mainData = []) {
  const rows = normalizeCaseMatrix(mainData, 0, 24);
  const entries = parseCaseEntries(rows).map((entry, index) => {
    const pathLabel = getCaseTraceEntryPathLabel(entry);
    const fromHash = getCaseTraceEntryFromHash(entry);
    return {
      ...entry,
      index,
      pathLabel,
      fromHash,
      explicitDirection: detectCaseTraceDirectionForEntry(entry),
      isRoot: !fromHash || isMainTransactionPathLabel(pathLabel)
    };
  });

  const rootEntries = entries
    .filter(entry => entry.isRoot)
    .map(entry => ({ ...entry, rootKey: `root-${entry.startRow}-${entry.index}` }));

  const rootForEntry = new Map();
  entries.forEach((entry) => {
    const directRoot = findCaseTraceRootEntry(entry, rootEntries);
    if (directRoot) rootForEntry.set(entry, directRoot);
  });

  // Path labels are normally enough. This fallback follows Column O for an
  // unusual or temporarily unlabeled entry without flattening the row model.
  let changed = true;
  let passes = 0;
  while (changed && passes < entries.length + 1) {
    changed = false;
    passes += 1;
    entries.forEach((entry) => {
      if (rootForEntry.has(entry)) return;
      const parent = findCaseTraceParentEntry(entry, entries);
      if (!parent) return;
      const inheritedRoot = rootForEntry.get(parent);
      if (inheritedRoot) {
        rootForEntry.set(entry, inheritedRoot);
        changed = true;
      }
    });
  }

  entries.forEach((entry) => {
    if (!rootForEntry.has(entry)) {
      const selfRoot = rootEntries.find(root => root.startRow === entry.startRow) || null;
      if (selfRoot) rootForEntry.set(entry, selfRoot);
    }
  });

  const entriesByRoot = new Map();
  entries.forEach((entry) => {
    const root = rootForEntry.get(entry) || null;
    const key = root?.rootKey || `orphan-${entry.startRow}-${entry.index}`;
    if (!entriesByRoot.has(key)) entriesByRoot.set(key, { root, entries: [] });
    entriesByRoot.get(key).entries.push(entry);
  });

  const rootDirectionByKey = new Map();
  entriesByRoot.forEach((group, key) => {
    const rootDirection = group.root?.explicitDirection || '';
    if (rootDirection) {
      rootDirectionByKey.set(key, rootDirection);
      return;
    }

    let inheritedDirection = '';
    group.entries
      .slice()
      .sort((left, right) => left.startRow - right.startRow)
      .forEach((entry) => {
        if (entry.explicitDirection) inheritedDirection = entry.explicitDirection;
      });
    rootDirectionByKey.set(key, inheritedDirection || CASE_TRACE_DIRECTION_FORWARD);
  });

  const contextByRowIndex = new Map();
  entriesByRoot.forEach((group, key) => {
    const rootDirection = rootDirectionByKey.get(key) || CASE_TRACE_DIRECTION_FORWARD;
    group.entries.forEach((entry) => {
      const traceDirection = entry.explicitDirection || rootDirection || CASE_TRACE_DIRECTION_FORWARD;
      const context = {
        traceDirection,
        isReverse: traceDirection === CASE_TRACE_DIRECTION_REVERSE,
        isForward: traceDirection !== CASE_TRACE_DIRECTION_REVERSE,
        explicitDirection: entry.explicitDirection || '',
        rootPathLabel: normalizeCell(group.root?.pathLabel),
        entryPathLabel: normalizeCell(entry.pathLabel),
        rootStartRow: Number(group.root?.startRow),
        entryStartRow: Number(entry.startRow)
      };
      (entry.rows || []).forEach((item) => {
        if (Number.isInteger(item?.rowIndex)) contextByRowIndex.set(item.rowIndex, context);
      });
    });
  });

  return contextByRowIndex;
}

export function getCaseTraceDirectionForEntry(entry = {}, fallback = CASE_TRACE_DIRECTION_FORWARD) {
  return detectCaseTraceDirectionForEntry(entry) || normalizeCaseTraceDirection(fallback) || CASE_TRACE_DIRECTION_FORWARD;
}

export function getCaseTraceEndpointSide(direction = CASE_TRACE_DIRECTION_FORWARD) {
  return normalizeCaseTraceDirection(direction) === CASE_TRACE_DIRECTION_REVERSE ? 'from' : 'to';
}

export function getCaseTraceEndpointAddress(row = [], direction = CASE_TRACE_DIRECTION_FORWARD) {
  return normalizeCell(row?.[getCaseTraceEndpointSide(direction) === 'from' ? 2 : 3]);
}

export function getCaseTraceDirectionRelevantAttribution(row = [], direction = CASE_TRACE_DIRECTION_FORWARD) {
  const traceDirection = normalizeCaseTraceDirection(direction) || CASE_TRACE_DIRECTION_FORWARD;
  if (traceDirection !== CASE_TRACE_DIRECTION_REVERSE) return normalizeCell(row?.[10]);

  const fromAttribution = normalizeCell(row?.[23]);
  if (fromAttribution) return fromAttribution;

  // Compatibility with an older structured Column K value such as
  // "from: Bridgers | to: Crypto.com Deposit".
  const structured = normalizeCell(row?.[10]);
  const match = structured.match(/(?:^|\|)\s*from\s*:\s*([^|]+)/i);
  return match ? normalizeCell(match[1]) : '';
}
